import React, { useReducer, Dispatch, SetStateAction, useEffect } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import dashboardStyles from '../../styles';
import Api from '../../API';
import { useInfiniteQuery } from '@tanstack/react-query';
import { theme } from '../../../../ThemeContext/ThemeObject';
import EventItem from './components/EventItem';
import Loader from '../Loader';
import { copyEventLinks } from './copyEventLinks';
import { FileCopy as FileCopyIcon, Done as DoneIcon } from '@mui/icons-material';
import { InitialScoutCalendarState, ScoutCalendarReducer } from '../../../../ScoutCalendar/reducer';
import ViewEvent from '../../../../ScoutCalendar/ViewEvent';
import CreateEvent from '../../../../ScoutCalendar/CreateEvent/CreateEvent';
import { NoEventsSVG } from '../../../Components/Icons';
import { styles } from './styles';
import { EmptyState } from '../../../Components/CustomUIElements/EmptyState';
import formatToUserTimeZone from '../../../utils/formatToUserTimeZone';

function UpcomingEvents({
  setSnackbar,
  apiKey,
  currentUserId,
  newCalendarEnabled
}: {
  setSnackbar: Dispatch<
    SetStateAction<{ message: string; state: 'success' | 'warning' | 'error' }>
  >;
  apiKey: string;
  currentUserId: number;
  newCalendarEnabled: boolean;
}) {
  const [ScoutCalendarState, dispatch] = useReducer(
    ScoutCalendarReducer,
    InitialScoutCalendarState
  );
  const [copied, setCopied] = React.useState(false);
  const { isLoading, isSuccess, data, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['getEvents'],
      queryFn: ({ pageParam = 1 }) => Api.getEvents({ page: pageParam }),
      getNextPageParam: (lastPage) => lastPage.nextPage || undefined
    });

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!e) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight) {
      if (!isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    }
  }

  function calculateAllItems(data: any, hasNextPage: boolean) {
    const items = data?.pages.flatMap((page: any) => page.data.events) || [];
    if (!items.length) return [];
    return items.map((item: any, index: number) => {
      const dateCurrent = formatToUserTimeZone(items[index].begins_at, 'ymd');
      const datePrevious = formatToUserTimeZone(items[index - 1]?.begins_at, 'ymd');
      const dateNext = formatToUserTimeZone(items[index + 1]?.begins_at, 'ymd');
      const isFirst = dateCurrent !== datePrevious;
      const isLast = dateCurrent !== dateNext || (index === items.length - 1 && !hasNextPage);
      return {
        ...item,
        isFirst,
        isLast
      };
    });
  }

  const allItems = calculateAllItems(data, hasNextPage);

  const hasUpcomingEvent = !!allItems?.length;

  return (
    <Box sx={{ ...dashboardStyles.section, ...styles.section }}>
      <Box sx={styles.headerContainer}>
        <Box sx={{ ...dashboardStyles.sectionTitle }}>Upcoming Events</Box>
        {hasUpcomingEvent && (
          <Tooltip title={copied ? 'Copied!' : 'Copy event links'} placement="top">
            <IconButton
              onClick={() => {
                copyEventLinks(allItems);
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 1000);
              }}
            >
              {copied ? (
                <DoneIcon sx={{ color: theme.palette.action.main }} />
              ) : (
                <FileCopyIcon sx={{ color: theme.palette.common.darkGrey }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={styles.taskContainer} onScroll={handleScroll} mr={4} ml={4}>
        {isLoading &&
          Array(3)
            .fill(null)
            .map((_, i) => <Loader key={i} />)}
        {isSuccess && (
          <>
            {hasUpcomingEvent ? (
              <>
                {allItems.map((item) => (
                  <EventItem
                    key={item.id}
                    event={item}
                    ScoutCalendarState={ScoutCalendarState}
                    dispatch={dispatch}
                    newCalendarEnabled={newCalendarEnabled}
                  />
                ))}
                {isFetchingNextPage && hasNextPage && (
                  <Box display="flex" flex={1} justifyContent="center">
                    <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />
                  </Box>
                )}
              </>
            ) : (
              <EmptyState svgIcon={<NoEventsSVG />} label="No upcoming events" />
            )}
          </>
        )}
      </Box>
      {ScoutCalendarState.showCreateEventModal && (
        <CreateEvent
          events={allItems}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={dispatch}
          setSnackbar={setSnackbar}
        />
      )}
      {ScoutCalendarState.showViewEventModal && (
        <ViewEvent
          events={allItems}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={dispatch}
          setSnackbar={setSnackbar}
          currentUserId={currentUserId}
        />
      )}
    </Box>
  );
}

export default UpcomingEvents;
